import { Box, SelectChangeEvent, Skeleton, Typography } from '@mui/material'
import theme from 'theme'
import { ApplicationManifest, IntegrationsSort } from 'models'
import {
  AppContainer,
  ControlsSection,
  FiltersContainer,
  GridContainer,
  Header,
  IntegrationsGridWrapper,
  MainContent,
  ResetButton,
  ScrollableContent,
  StyledTab,
  StyledTabs
} from '../../integrations.styles'
import {
  integrationsInstancesOptions,
  integrationsSortOptionsInstances,
  integrationsSortOptionsIntegrations
} from '../../integrations.constants'
import { IntegrationsSearch, IntegrationsSelectCustom, IntegrationsSelectField } from '../../form-components'
import SortButtonSelect from 'ui/components/form-components/SortButtonSelect'
import EmptyState from '../../../../coverage-analyzer/components/EmptyState'
import { SSMCardSkeleton, SSMCardSkeletonWrapper } from '../../../../security-stack-map/components/ssmCard.styles'
import ApplicationCard from './components/ApplicationCard'
import ApplicationDrawer from './components/ApplicationDrawer'
import {
  ALL_SECTION_TITLE,
  PRODUCT_TYPE_LABEL,
  CONFIGURATION_STATUS_LABEL,
  CatalogTab,
  DIRECTORY_TAB_LABEL,
  EMPTY_TITLE,
  INSTANCES_TAB_LABEL,
  ITEM_COUNT_LABEL,
  NEW_SECTION_TITLE,
  NO_INSTANCES_DESCRIPTION,
  NO_INTEGRATIONS_DESCRIPTION,
  NO_MATCH_DESCRIPTION,
  PAGE_TITLE,
  PRODUCT_LABEL,
  RESET_ALL_LABEL,
  SEARCH_PLACEHOLDER,
  VENDOR_LABEL
} from './applicationsCatalog.constants'
import { useApplicationsCatalog } from './useApplicationsCatalog'

/* The multi-selects answer a comma-joined string when the browser autofills them. */
const selectedValues = (event: SelectChangeEvent<string[]>): string[] => {
  const { value } = event.target
  return typeof value === 'string' ? value.split(',') : value
}

const ApplicationsCatalog = () => {
  const {
    tab,
    changeTab,
    loading,
    resetKey,
    catalogEmpty,
    newManifests,
    allManifests,
    instanceCounts,
    directorySearch,
    setDirectorySearch,
    directoryFilters,
    setDirectoryFilter,
    directorySort,
    setDirectorySort,
    directoryOptions,
    resetDirectory,
    noInstances,
    instances,
    instanceSearch,
    setInstanceSearch,
    instanceFilters,
    setInstanceFilter,
    instanceSort,
    setInstanceSort,
    instanceOptions,
    resetInstances,
    directoryCount,
    instancesCount,
    addInstance,
    viewInstances,
    paneOpen,
    paneManifest,
    paneApplication,
    paneInstances,
    openManifestPane,
    openInstancePane,
    closePane,
    noEditPermission,
    confirmRemove
  } = useApplicationsCatalog()

  const renderCards = (manifests: ApplicationManifest[], isNew: boolean) => (
    <GridContainer>
      {manifests.map((manifest) => (
        <ApplicationCard
          key={manifest.id}
          manifest={manifest}
          isNew={isNew}
          instanceCount={instanceCounts[manifest.id] || 0}
          searchValue={directorySearch}
          onAddInstance={addInstance}
          onViewInstances={viewInstances}
          onOpen={openManifestPane}
        />
      ))}
    </GridContainer>
  )

  const renderSectionTitle = (title: string) => (
    <Box className="title-wrapper">
      <Typography variant="h5" className="integrations-category-title" fontWeight={500}>
        {title}
      </Typography>
    </Box>
  )

  const renderSkeletons = () => (
    <IntegrationsGridWrapper>
      <GridContainer>
        {Array.from(Array(20).keys()).map((i) => (
          <SSMCardSkeletonWrapper sx={{ gap: `${theme.spacing.xSmall}px !important` }} key={i}>
            <SSMCardSkeleton sx={{ height: '200px !important' }} className="integrations">
              <Skeleton
                variant="rectangular"
                component="div"
                animation="wave"
                width="100%"
                height="100%"
                className="card-skeleton-inner"
              />
            </SSMCardSkeleton>
          </SSMCardSkeletonWrapper>
        ))}
      </GridContainer>
    </IntegrationsGridWrapper>
  )

  const renderDirectoryControls = () => (
    <ControlsSection className="controls-section">
      <FiltersContainer className="filters-container">
        <IntegrationsSearch
          resetKey={`directory-${resetKey}`}
          searchValue={directorySearch}
          setSearchValue={setDirectorySearch}
          onInputChange={(_: unknown, value: string) => setDirectorySearch(value)}
          placeholder={SEARCH_PLACEHOLDER}
        />

        <IntegrationsSelectCustom
          label={PRODUCT_TYPE_LABEL}
          options={directoryOptions.category}
          placeholder={PRODUCT_TYPE_LABEL}
          value={directoryFilters.category}
          multiple
          handleReset={() => setDirectoryFilter('category', [])}
          field="category"
          handleChange={(e: SelectChangeEvent<string[]>) => setDirectoryFilter('category', selectedValues(e))}
          maxHeight="296px"
        />

        <IntegrationsSelectCustom
          label={VENDOR_LABEL}
          options={directoryOptions.vendor}
          placeholder="vendor"
          value={directoryFilters.vendor}
          multiple
          handleReset={() => setDirectoryFilter('vendor', [])}
          field="vendor"
          handleChange={(e: SelectChangeEvent<string[]>) => setDirectoryFilter('vendor', selectedValues(e))}
          maxHeight="296px"
        />

        <IntegrationsSelectField
          label={CONFIGURATION_STATUS_LABEL}
          options={integrationsInstancesOptions}
          placeholder="configuration status"
          value={directoryFilters.configurationStatus}
          handleChange={(e: SelectChangeEvent<string[]>) =>
            setDirectoryFilter('configurationStatus', selectedValues(e))
          }
          multiple
        />

        <ResetButton variant="outlined" onClick={resetDirectory}>
          {RESET_ALL_LABEL}
        </ResetButton>
      </FiltersContainer>

      <Box className="sort-wrapper">
        <Typography className="number-of-items" variant="body2">
          {ITEM_COUNT_LABEL} <span>{directoryCount}</span>
        </Typography>

        <SortButtonSelect
          options={integrationsSortOptionsIntegrations}
          value={directorySort}
          handleChange={(e: SelectChangeEvent<IntegrationsSort>) =>
            setDirectorySort(e.target.value as IntegrationsSort)
          }
        />
      </Box>
    </ControlsSection>
  )

  const renderInstanceControls = () => (
    <ControlsSection className="controls-section">
      <FiltersContainer className="filters-container">
        <IntegrationsSearch
          resetKey={`instances-${resetKey}`}
          searchValue={instanceSearch}
          setSearchValue={setInstanceSearch}
          onInputChange={(_: unknown, value: string) => setInstanceSearch(value)}
          placeholder={SEARCH_PLACEHOLDER}
        />

        <IntegrationsSelectCustom
          label={PRODUCT_TYPE_LABEL}
          options={instanceOptions.category}
          placeholder={PRODUCT_TYPE_LABEL}
          value={instanceFilters.category}
          multiple
          handleReset={() => setInstanceFilter('category', [])}
          field="category"
          handleChange={(e: SelectChangeEvent<string[]>) => setInstanceFilter('category', selectedValues(e))}
          maxHeight="296px"
        />

        <IntegrationsSelectCustom
          label={VENDOR_LABEL}
          options={instanceOptions.vendor}
          placeholder="vendor"
          value={instanceFilters.vendor}
          multiple
          handleReset={() => setInstanceFilter('vendor', [])}
          field="vendor"
          handleChange={(e: SelectChangeEvent<string[]>) => setInstanceFilter('vendor', selectedValues(e))}
          maxHeight="296px"
        />

        <IntegrationsSelectCustom
          label={PRODUCT_LABEL}
          options={instanceOptions.product}
          placeholder=""
          value={instanceFilters.product}
          multiple
          handleReset={() => setInstanceFilter('product', [])}
          field="product"
          handleChange={(e: SelectChangeEvent<string[]>) => setInstanceFilter('product', selectedValues(e))}
          maxHeight="296px"
        />

        <ResetButton variant="outlined" onClick={resetInstances}>
          {RESET_ALL_LABEL}
        </ResetButton>
      </FiltersContainer>

      <Box className="sort-wrapper">
        <Typography className="number-of-items" variant="body2">
          {ITEM_COUNT_LABEL} <span>{instancesCount}</span>
        </Typography>

        <SortButtonSelect
          options={integrationsSortOptionsInstances}
          value={instanceSort}
          handleChange={(e: SelectChangeEvent<IntegrationsSort>) => setInstanceSort(e.target.value as IntegrationsSort)}
        />
      </Box>
    </ControlsSection>
  )

  const renderInstances = () => {
    if (!instancesCount) {
      return (
        <EmptyState
          title={EMPTY_TITLE}
          description={noInstances ? NO_INSTANCES_DESCRIPTION : NO_MATCH_DESCRIPTION}
          variant="instances"
        />
      )
    }

    return (
      <IntegrationsGridWrapper className="integrations-list-wrapper">
        <GridContainer>
          {instances.map(({ application, manifest }) => (
            <ApplicationCard
              key={application.id}
              application={application}
              manifest={manifest}
              searchValue={instanceSearch}
              onOpen={openInstancePane}
            />
          ))}
        </GridContainer>
      </IntegrationsGridWrapper>
    )
  }

  const renderDirectory = () => {
    if (!directoryCount) {
      return (
        <EmptyState
          title={EMPTY_TITLE}
          description={catalogEmpty ? NO_INTEGRATIONS_DESCRIPTION : NO_MATCH_DESCRIPTION}
          variant="instances"
        />
      )
    }

    return (
      <>
        {newManifests.length > 0 && (
          <IntegrationsGridWrapper className="integrations-list-wrapper">
            {renderSectionTitle(NEW_SECTION_TITLE)}
            {renderCards(newManifests, true)}
          </IntegrationsGridWrapper>
        )}

        {allManifests.length > 0 && (
          <IntegrationsGridWrapper>
            {/* As on the inbound page, the heading only separates All from a New section above it. */}
            {newManifests.length > 0 && renderSectionTitle(ALL_SECTION_TITLE)}
            {renderCards(allManifests, false)}
          </IntegrationsGridWrapper>
        )}
      </>
    )
  }

  return (
    <AppContainer className="app-container">
      <MainContent className="main-content">
        <Header className="header">
          <Typography variant="h1" className="title" fontWeight={500} mb={4}>
            {PAGE_TITLE}
          </Typography>

          <StyledTabs value={tab} onChange={(_, value: CatalogTab) => changeTab(value)}>
            <StyledTab label={`${DIRECTORY_TAB_LABEL} (${directoryCount})`} />
            <StyledTab label={`${INSTANCES_TAB_LABEL} (${instancesCount})`} />
          </StyledTabs>
        </Header>

        {tab === CatalogTab.Directory ? (
          <>
            {renderDirectoryControls()}
            <ScrollableContent className="scrollable-content">
              {loading ? renderSkeletons() : renderDirectory()}
            </ScrollableContent>
          </>
        ) : (
          <>
            {renderInstanceControls()}
            <ScrollableContent className="scrollable-content">
              {loading ? renderSkeletons() : renderInstances()}
            </ScrollableContent>
          </>
        )}
      </MainContent>

      <ApplicationDrawer
        open={paneOpen}
        manifest={paneManifest}
        application={paneApplication}
        instances={paneInstances}
        noEditPermission={noEditPermission}
        onClose={closePane}
        onViewInstances={viewInstances}
        onAddInstance={addInstance}
        onRemove={confirmRemove}
      />
    </AppContainer>
  )
}

export default ApplicationsCatalog
